/**
 * ThumbnailSlider component - Displays and manages multiple captured images
 *
 * Features:
 * - Main image slider with swipe/gesture support
 * - Horizontal scrolling thumbnail navigation
 * - Active image highlighting with transparent overlay
 * - Delete functionality for active images
 * - Smooth scrolling and auto-centering
 * - Responsive touch interactions
 *
 * Props:
 * @param {Array} selectedImages - Array of captured image objects with metadata
 * @param {string|null} activeImageId - ID of currently active/selected image
 * @param {Function} setActiveImageId - Function to update active image selection
 * @param {Function} setSelectedImages - Function to update images array
 */
import Slider from "react-slick";
import { useEffect, useRef } from "react";
import "slick-carousel/slick/slick.css";
import "slick-carousel/slick/slick-theme.css";
import cancel from "../../../assets/cancel.svg";

const ThumbnailSlider = ({
  selectedImages, // Array of captured image objects with metadata
  activeImageId, // Currently active image ID for display
  setActiveImageId, // Function to update active image selection
  setSelectedImages, // Function to modify images array
}) => {
  // Reference to main slider component for programmatic control
  const sliderRef = useRef(null);
  // Reference to thumbnail container for scroll operations
  const thumbRef = useRef(null);

  // Configuration for react-slick main image slider
  const settings = {
    dots: false, // Hide dot pagination
    infinite: false, // Disable infinite scrolling
    speed: 350, // Slide transition speed in ms
    slidesToShow: 1, // Show one slide at a time
    slidesToScroll: 1, // Scroll one slide at a time
    arrows: false, // Hide navigation arrows
    swipe: true, // Enable swipe gestures
    draggable: true, // Enable mouse drag
    touchMove: true, // Enable touch move gestures

    // Callback fired after slide change
    afterChange: (index) => {
      const img = selectedImages[index];
      // Update active image ID when slider changes
      if (img && img.id !== activeImageId) {
        setActiveImageId(img.id);
      }
    },
  };

  /**
   * Handles thumbnail click events
   * Navigates main slider to the clicked thumbnail's image
   * @param {number} index - Index of the clicked thumbnail
   */
  const handleThumbnailClick = (index) => {
    sliderRef.current?.slickGoTo(index);
  };

  /**
   * Handles image deletion from thumbnail
   * Removes image from array and updates active selection if needed
   * @param {string} imageId - ID of the image to delete
   * @param {Event} e - Click event to stop propagation
   */
  const handleDeleteImage = (imageId, e) => {
    e.stopPropagation(); // Prevent thumbnail click event
    
    // Remove image from selectedImages array
    setSelectedImages(prev => prev.filter(img => img.id !== imageId));
    
    // If deleted image was active, set new active image
    if (imageId === activeImageId) {
      const remainingImages = selectedImages.filter(img => img.id !== imageId);
      // Clear active selection if no images remain
      if (!remainingImages.length) {
        setActiveImageId(null);
      } 
    }
  };

  /**
   * Synchronizes slider and thumbnail scroll when active image changes
   * Ensures main slider shows active image and thumbnail is centered
   */
  useEffect(() => {
    // Find index of active image in the array
    const index = selectedImages.findIndex(
      (img) => img.id === activeImageId
    );
    
    if (index >= 0) {
      // Delay to ensure DOM updates are complete before scrolling
      setTimeout(() => {
        // Navigate main slider to active image
        sliderRef.current?.slickGoTo(index);
  
        // Scroll active thumbnail into center view
        const el = thumbRef.current?.children[index];
        el?.scrollIntoView({
          behavior: "smooth", // Smooth scrolling animation
          inline: "center",   // Center horizontally
          block: "nearest",  // Minimal vertical scrolling
        });
      }, 100); // 100ms delay for smooth animation
    }
  }, [activeImageId, selectedImages]); // Dependencies: re-run when active image or images change

  return (
    <div className="thumb-slider-wrapper">
      {/* MAIN SLIDER - Shows full-size images */}
      {selectedImages.length > 1 ? (
        <Slider ref={sliderRef} {...settings}>
          {selectedImages.map((img) => (
            <div key={img.id}>
              <div
                className="main-image-display"
                style={{ backgroundImage: `url(${img.filtered})` }}
              />
            </div>
          ))}
        </Slider>
      ) : (
        // Single image display - no slider needed
        <div
          className="main-image-display"
          style={{ backgroundImage: `url(${selectedImages[0]?.filtered})` }}
        />
      )}

      {/* HORIZONTAL SCROLL THUMBNAILS - Navigation and deletion */}
      {selectedImages.length > 1 && (
        <div className="thumbnail-pagination" ref={thumbRef}>
          {selectedImages.map((img, index) => (
            <button
              key={img.id}
              className={`thumbnail-dot ${
                img.id === activeImageId ? "active" : ""
              }`}
              onClick={() => handleThumbnailClick(index)}
            >
              {/* Thumbnail image preview */}
              <div
                className="thumbnail-image"
                style={{ backgroundImage: `url(${img.filtered})` }}
              />
              
              {/* Delete button - only shown on active image */}
              {img.id === activeImageId && (
                <button
                  className="thumbnail-delete-btn"
                  onClick={(e) => handleDeleteImage(img.id, e)}
                  title="Delete image"
                >
                  <img src={cancel} alt="Delete" />
                </button>
              )}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

export default ThumbnailSlider;
